import { useCallback, useRef, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  Paper,
  PaperProps,
  TextField,
  Typography,
} from "@mui/material";
import {
  SecuritySearchResult,
  StockSearchResult,
  useSearchSecuritiesQuery,
  useSearchStocksQuery,
} from "features/trading/accounts";
import { useDebounce, useDebouncedCallback } from "use-debounce";

const SEARCH_DEBOUNCE_MS = 250;

// The endpoint's page size, sent on every request. Without it DRF's default
// page size of 10 applies, and the truncation footer below would announce a
// hundred rows above a list of ten.
export const STOCK_SEARCH_PAGE_SIZE = 100;

export const NO_OPTIONS_TEXT = "No security matches the input pattern";
export const FIELD_LABEL = "Stock Symbol:";

export const truncationFooterText = (shown: number, total: number) =>
  `Showing the first ${shown} of ${total} matches — type more characters to narrow the list`;

interface Props {
  /** Called with the chosen symbol, or "" when the selection is cleared. */
  onSymbolChange?: (symbol: string) => void;
  /** Called with the chosen row, or null when the selection is cleared. */
  onSecurityChange?: (security: SearchResult | null) => void;
  /**
   * Search option contracts as well as stocks and ETFs (the Securities page's
   * Quotes tab). Each row then carries its kind, shown beside the symbol.
   */
  includeOptions?: boolean;
  label?: string;
}

export type SearchResult = StockSearchResult & Partial<Pick<SecuritySearchResult, "kind">>;

/**
 * The Option Chain page's symbol picker.
 *
 * Selecting a row is the only way to commit a symbol: there is no freeSolo, so
 * text that matches nothing can never load a chain. That is the settled
 * behaviour, not an oversight — a symbol absent from the assets table is
 * unreachable from this page.
 */
export default function StockSymbolAutocomplete({
  onSymbolChange,
  onSecurityChange,
  includeOptions = false,
  label = FIELD_LABEL,
}: Props) {
  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const trimmed = inputValue.trim();
  const [debouncedPattern] = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);

  const searchArgs = { pattern: debouncedPattern, page_size: STOCK_SEARCH_PAGE_SIZE };
  const stocks = useSearchStocksQuery(searchArgs, {
    skip: includeOptions || debouncedPattern.length === 0,
  });
  const securities = useSearchSecuritiesQuery(searchArgs, {
    skip: !includeOptions || debouncedPattern.length === 0,
  });
  const { data, isFetching } = includeOptions ? securities : stocks;

  const results: SearchResult[] = data?.results ?? [];
  const count = data?.count ?? 0;

  // True from the first keystroke until the results for that exact input have
  // arrived. Drives the spinner AND makes Enter inert, so autoHighlight cannot
  // commit a row left over from the previous pattern.
  const isSettling = debouncedPattern !== trimmed || isFetching;

  // Clearing the chain is debounced; committing a selection is not. The flag is
  // explicit rather than derived from inputValue, because after a selection MUI
  // rewrites inputValue to the chosen symbol (reason "reset") — a clear driven
  // off inputValue would therefore settle back onto the symbol it just cleared.
  const clearSymbol = useDebouncedCallback(() => {
    setSelected(null);
    onSymbolChange?.("");
    onSecurityChange?.(null);
  }, SEARCH_DEBOUNCE_MS);

  const clearNow = useCallback(() => {
    clearSymbol.cancel();
    setSelected(null);
    setInputValue("");
    onSymbolChange?.("");
    onSecurityChange?.(null);
  }, [clearSymbol, onSymbolChange, onSecurityChange]);

  // Truncation is detected as "the server has more than it sent", not as
  // "more than 100": only the former stays honest if the page size ever
  // changes underneath us.
  const showFooter = count > results.length;

  // Read through a ref so the callback below can keep a dependency list of
  // [showFooter] alone. An identity that changed with the numbers would remount
  // the Paper — and reset the listbox scroll — every time a result set landed.
  const footerTextRef = useRef("");
  footerTextRef.current = truncationFooterText(results.length, count);

  const PaperWithFooter = useCallback(
    (paperProps: PaperProps) => (
      <Paper
        {...paperProps}
        sx={{
          // Grow past the input when a symbol needs it — the longest eligible
          // symbol is 16 characters and does not fit the field's own width on a
          // narrow screen. The widest SYMBOL is what may stretch the popup; the
          // footer is excluded from that measurement below, since sizing to a
          // whole sentence would push the popup off a 390px viewport.
          width: "max-content",
          minWidth: "100%",
          maxWidth: "min(320px, calc(100vw - 32px))",
        }}
      >
        {paperProps.children}
        {showFooter && (
          <Typography
            variant="caption"
            component="div"
            sx={{
              px: 2,
              py: 1,
              color: "text.secondary",
              // Zero width with a 100% floor: the sentence still fills the
              // popup, but contributes nothing to the max-content measurement
              // above, so the popup is sized by the symbols and not by this.
              width: 0,
              minWidth: "100%",
            }}
          >
            {footerTextRef.current}
          </Typography>
        )}
      </Paper>
    ),
    [showFooter],
  );

  return (
    <Autocomplete<SearchResult>
      options={results}
      // The server already filtered and ranked; MUI must not do either again.
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.symbol}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={selected}
      inputValue={inputValue}
      loading={isSettling}
      autoHighlight
      // An empty box shows nothing below it, so there is no arrow inviting a
      // click that could only reveal the no-match copy.
      forcePopupIcon={false}
      open={isOpen && trimmed.length > 0}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      noOptionsText={NO_OPTIONS_TEXT}
      PaperComponent={PaperWithFooter}
      onInputChange={(_, next, reason) => {
        // "reset" is MUI rewriting the box itself — after a selection, and when
        // clearOnBlur restores the last committed symbol. Neither is the user
        // typing, and treating the restore as typing would clear a live chain.
        if (reason === "reset") return;
        setInputValue(next);
        if (reason === "input") {
          // The chain belongs to the previous symbol. Drop it once the
          // keystrokes settle, matching the search's own debounce.
          setSelected(null);
          clearSymbol();
        }
      }}
      onChange={(_, option, reason) => {
        clearSymbol.cancel();
        if (!option) {
          // MUI fires this with null on the ✕, on Escape, and on backspacing the
          // box empty. Explicit gestures clear at once, ahead of the debounce.
          clearNow();
          return;
        }
        setSelected(option);
        setInputValue(option.symbol);
        onSymbolChange?.(option.symbol);
        onSecurityChange?.(option);
        if (reason === "selectOption") setIsOpen(false);
      }}
      renderOption={(optionProps, option) => (
        <li
          {...optionProps}
          key={option.id}
          // A symbol is one token: never break it across lines (delisted-style
          // symbols carry a hyphen, which is otherwise a wrap opportunity) and
          // never ellipsise it. Keeping it unbreakable is also what lets the
          // Paper's max-content width above size itself to the whole symbol.
          // "pre", not "nowrap": an option contract pads its root with runs of
          // spaces, which "nowrap" would collapse into one.
          style={{ ...optionProps.style, whiteSpace: "pre", textOverflow: "clip" }}
        >
          {option.symbol}
          {option.kind && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: "auto", pl: 2 }}
            >
              {option.kind === "option" ? "Option" : "Stock"}
            </Typography>
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          sx={{ minWidth: { xs: "100%", sm: 260 } }}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              // While the results are still catching up, the highlighted row is
              // the previous pattern's. Swallow Enter rather than commit it.
              if (event.key === "Enter" && isSettling) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }
              (params.inputProps as any).onKeyDown?.(event);
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSettling ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
